import React, { useMemo, useEffect, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SafeIcon from '../common/SafeIcon';
import { FiMapPin, FiNavigation, FiMaximize2, FiActivity, FiSearch, FiBriefcase, FiStar } from 'react-icons/fi';
import JobDrawer from '../components/jobs/JobDrawer';
import { renderToString } from 'react-dom/server';

const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const createFeaturedIcon = () => {
  const html = renderToString(
    <div className="relative flex items-center justify-center">
      <div className="absolute w-10 h-10 bg-amber-400/40 rounded-full animate-ping" />
      <div className="relative bg-amber-500 w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white transform -translate-y-1">
        <FiStar className="text-white text-sm fill-current" />
        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-amber-500" />
      </div>
    </div>
  );

  return L.divIcon({
    html: html,
    className: 'custom-featured-pin',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};

const RecenterButton = ({ center }) => {
  const map = useMap();
  return (
    <button 
      onClick={() => map.setView(center, 2)}
      className="absolute bottom-6 right-6 z-[1000] bg-white p-3 rounded-xl shadow-xl border border-stone-200 text-emerald-600 hover:text-emerald-700 transition-all hover:scale-110 active:scale-95"
      title="Recenter Map"
    >
      <SafeIcon icon={FiMaximize2} className="text-xl" />
    </button>
  );
};

const MapView = () => {
  const { jobs, loading } = useAppContext();
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const validLocations = useMemo(() => {
    if (!jobs) return [];
    const locationsList = [];
    jobs.forEach(job => {
      const isExpired = job.expires_at && new Date(job.expires_at) < new Date();
      const isVisible = job.status === 'active' && !isExpired;

      if (!isVisible) return;

      if (job.locations && Array.isArray(job.locations)) {
        job.locations.forEach(loc => {
          if (loc.lat && loc.lng) {
            locationsList.push({ ...loc, job: job });
          }
        });
      }
    });

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return locationsList.filter(loc => 
        loc.job.title.toLowerCase().includes(q) ||
        loc.name.toLowerCase().includes(q) ||
        loc.job.profile?.org_name?.toLowerCase().includes(q)
      );
    }

    return locationsList;
  }, [jobs, searchQuery]);

  const defaultCenter = [20, 0]; // More neutral center for world view

  if (loading) return (
    <div className="flex-1 flex items-center justify-center bg-stone-50 min-h-[500px]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-stone-500 font-medium tracking-wide">Mapping Jobs...</p>
      </div>
    </div>
  );

  return (
    <div className="flex-1 relative flex flex-col min-h-[600px]">
      <div className="absolute top-6 left-6 right-6 z-[1000] max-w-xl mx-auto md:mx-0">
        <div className="bg-white/90 backdrop-blur-md p-2 rounded-2xl shadow-2xl border border-white/50 flex items-center gap-3">
          <div className="flex-1 flex items-center px-4 py-2.5 bg-stone-50/50 rounded-xl border border-stone-100">
            <SafeIcon icon={FiSearch} className="text-stone-400 mr-2" />
            <input 
              type="text" 
              placeholder="Search job destinations..." 
              className="bg-transparent border-none focus:ring-0 text-sm font-bold text-stone-800 placeholder:text-stone-400 w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="hidden sm:flex items-center gap-2 px-4 py-2 border-l border-stone-200">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-black uppercase text-stone-500 tracking-widest">{validLocations.length} Points</span>
          </div>
        </div>
      </div>

      <div className="flex-1 relative">
        <MapContainer center={defaultCenter} zoom={2} className="absolute inset-0 z-0" zoomControl={false}>
          <TileLayer
            attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />
          {validLocations.map((loc, idx) => (
            <Marker 
              key={`${loc.job_id}-${idx}`} 
              position={[loc.lat, loc.lng]} 
              icon={loc.job.is_featured ? createFeaturedIcon() : customIcon}
              eventHandlers={{ 
                click: (e) => { 
                  e.originalEvent.stopPropagation(); 
                  setSelectedJob(loc.job); 
                } 
              }}
            >
              <Tooltip direction="top" offset={[0, -32]} opacity={1} className="custom-job-tooltip">
                <div className="p-2 min-w-[200px] bg-white rounded-xl">
                  {loc.job.is_featured && (
                    <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-amber-50 rounded-lg border border-amber-200 w-fit">
                      <SafeIcon icon={FiStar} className="text-amber-500 text-[10px] fill-current" />
                      <span className="text-[9px] font-black uppercase tracking-widest text-amber-600">Featured</span>
                    </div>
                  )}
                  <h3 className="font-black text-stone-900 text-sm mb-0.5 leading-tight">{loc.job.title}</h3>
                  <p className="text-[10px] font-black text-emerald-600 uppercase tracking-wider mb-2">{loc.job.profile?.org_name}</p>
                  <div className="flex items-center gap-2 text-[9px] font-bold text-stone-400 uppercase tracking-tighter">
                    <SafeIcon icon={FiMapPin} className="text-emerald-500" />
                    {loc.name}
                  </div>
                  <div className="mt-3 pt-2 border-t border-stone-50 flex items-center justify-between">
                    <span className="text-[8px] font-black text-stone-300 uppercase tracking-widest">Click to view</span>
                    <SafeIcon icon={FiNavigation} className="text-emerald-500 text-xs" />
                  </div>
                </div>
              </Tooltip>
            </Marker>
          ))}
          <RecenterButton center={defaultCenter} />
        </MapContainer>
      </div>

      <JobDrawer job={selectedJob} isOpen={!!selectedJob} onClose={() => setSelectedJob(null)} />
    </div>
  );
};

export default MapView;